import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, BookmarkCheck, Trash2 } from "lucide-react";
import {
  getReadingHistory,
  clearReadingHistory,
  labelForType,
  formatProgressSubtitle,
} from "../lib/readingProgress";

const formatRelative = (iso) => {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const diffSec = Math.floor((Date.now() - then) / 1000);
  if (diffSec < 60) return "agora há pouco";
  if (diffSec < 3600) return `há ${Math.floor(diffSec / 60)} min`;
  if (diffSec < 86400) return `há ${Math.floor(diffSec / 3600)} h`;
  if (diffSec < 86400 * 7) return `há ${Math.floor(diffSec / 86400)} d`;
  return new Date(iso).toLocaleDateString("pt-BR");
};

export default function Historico() {
  const [items, setItems] = useState([]);

  useEffect(() => {
    setItems(getReadingHistory());
  }, []);

  const handleClear = () => {
    if (!window.confirm("Apagar todo o histórico de leitura?")) return;
    clearReadingHistory();
    setItems([]);
  };

  return (
    <div className="max-w-3xl mx-auto section-padding py-8" data-testid="history-page">
      <Link
        to="/"
        className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-navy/60 hover:text-navy mb-6"
        data-testid="history-back"
      >
        <ArrowLeft className="w-4 h-4" /> Início
      </Link>

      <div className="flex items-center justify-between mb-8 gap-4">
        <div>
          <p className="text-[10px] uppercase tracking-[0.3em] text-navy/50">Sua jornada</p>
          <h1 className="font-display text-4xl sm:text-5xl text-navy mt-2 leading-[1.05]">
            Histórico de leitura
          </h1>
        </div>
        {items.length > 0 && (
          <button
            type="button"
            onClick={handleClear}
            data-testid="history-clear"
            className="inline-flex items-center gap-2 text-[11px] uppercase tracking-widest text-navy/60 hover:text-destructive transition-colors"
          >
            <Trash2 className="w-3.5 h-3.5" /> Limpar
          </button>
        )}
      </div>

      {items.length === 0 ? (
        <div
          className="bg-white border border-border rounded-2xl p-8 text-center"
          data-testid="history-empty"
        >
          <BookmarkCheck className="w-8 h-8 text-gold/70 mx-auto" />
          <p className="mt-4 text-navy/70 text-base">
            Você ainda não possui histórico de leitura.
          </p>
          <p className="text-xs text-navy/50 mt-2">
            Abra um devocional, sermão ou um dia de plano para começar.
          </p>
        </div>
      ) : (
        <ul className="space-y-3" data-testid="history-list">
          {items.map((item) => (
            <li key={item.id || item.href}>
              <Link
                to={item.href}
                data-testid={`history-item-${item.id}`}
                className="group flex items-center gap-4 bg-white border border-border rounded-2xl p-5 card-lift"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-gold">
                    <span>{labelForType(item.type)}</span>
                    <span className="text-navy/30">·</span>
                    <span className="text-navy/50 normal-case tracking-normal">
                      {formatRelative(item.updated_at)}
                    </span>
                  </div>
                  <h3 className="font-display text-lg text-navy mt-1.5 leading-snug truncate">
                    {item.title || labelForType(item.type)}
                  </h3>
                  <p className="text-sm text-navy/65 mt-0.5 line-clamp-1">
                    {item.subtitle || formatProgressSubtitle(item)}
                  </p>
                </div>
                <ArrowRight className="w-4 h-4 text-navy/40 group-hover:text-gold group-hover:translate-x-0.5 transition" />
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
